import { useState, useEffect } from "react";

/**
 * This hook checks if the viewport matches the query passed.
 * Based on https://material-ui.com/components/use-media-query/.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList#Browser_compatibility
 */
const useMediaQuery = (query: string, options = {}) => {
  const supportMatchMedia =
    typeof window !== "undefined" && typeof window.matchMedia !== "undefined";

  const queryValue = query.replace(/^@media( ?)/m, "");

  const {
    defaultMatches = false,
    matchMedia = supportMatchMedia ? window.matchMedia : null,
  } = {
    ...options,
  };

  /**
   * @todo matchMedia(query).matches always returns false on first run.
   * Need to investigate. We could do this otherwise:
   * if (supportMatchMedia) {
   *   return matchMedia(query).matches;
   * }
   *
   */
  const [match, setMatch] = useState(() => {
    // Once the component is mounted, we rely on the
    // event listeners to return the correct matches value.
    return defaultMatches;
  });

  useEffect(() => {
    if (!supportMatchMedia) {
      return undefined;
    }

    const queryList = matchMedia(queryValue);

    const updateMatch = () => {
      setMatch(queryList.matches);
    };

    updateMatch();

    /** @todo replace with `add/removeEventListener when Safari supports it */
    queryList.addListener(updateMatch);

    return () => queryList.removeListener(updateMatch);
  }, [queryValue, matchMedia, supportMatchMedia]);

  return match;
};

export default useMediaQuery;
